import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

const insuranceProducts = [
  {
    title: 'Term Life Insurance',
    href: '/term-life',
    icon: 'ri-shield-check-line',
    description: 'Affordable coverage for a set period — ideal for income replacement, mortgage protection, and young families.',
    badge: 'Most Popular',
  },
  {
    title: 'Whole Life Insurance',
    href: '/whole-life',
    icon: 'ri-heart-pulse-line',
    description: 'Permanent coverage with guaranteed cash value growth. Protect your family for life.',
    badge: null,
  },
  {
    title: 'Final Expense Insurance',
    href: '/final-expense',
    icon: 'ri-hand-heart-line',
    description: 'Affordable burial and end-of-life coverage so your family isn\'t left with unexpected costs.',
    badge: null,
  },
  {
    title: 'Mortgage Protection',
    href: '/mortgage-protection',
    icon: 'ri-home-heart-line',
    description: 'Keep your family in their home if the unexpected happens. Covers your mortgage balance.',
    badge: null,
  },
  {
    title: 'Annuities',
    href: '/annuities',
    icon: 'ri-line-chart-line',
    description: 'Guaranteed retirement income you can\'t outlive. Grow your savings tax-deferred.',
    badge: null,
  },
  {
    title: 'Wealth Strategy Call',
    href: '/wealth-strategy-call',
    icon: 'ri-calendar-check-line',
    description: 'Book a free 1-on-1 strategy session with a licensed advisor to review your financial goals.',
    badge: 'Free',
  },
];

const resourcePages = [
  { title: 'FAQ', href: '/faq', icon: 'ri-question-answer-line', description: 'Answers to the most common life insurance questions.' },
  { title: 'Privacy Policy', href: '/privacy-policy', icon: 'ri-lock-line', description: 'How we collect, use, and protect your personal information.' },
  { title: 'Terms of Service', href: '/terms-of-service', icon: 'ri-file-text-line', description: 'Terms governing use of our website and services.' },
];

const cities = [
  { name: 'Indianapolis', slug: 'indianapolis', county: 'Marion County' },
  { name: 'Castleton', slug: 'castleton', county: 'Marion County' },
  { name: 'Broad Ripple', slug: 'broad-ripple', county: 'Marion County' },
  { name: 'Lawrence', slug: 'lawrence', county: 'Marion County' },
  { name: 'Beech Grove', slug: 'beech-grove', county: 'Marion County' },
  { name: 'Speedway', slug: 'speedway', county: 'Marion County' },
  { name: 'Geist', slug: 'geist', county: 'Marion / Hamilton County' },
  { name: 'Carmel', slug: 'carmel', county: 'Hamilton County' },
  { name: 'Fishers', slug: 'fishers', county: 'Hamilton County' },
  { name: 'Noblesville', slug: 'noblesville', county: 'Hamilton County' },
  { name: 'Westfield', slug: 'westfield', county: 'Hamilton County' },
  { name: 'Avon', slug: 'avon', county: 'Hendricks County' },
  { name: 'Plainfield', slug: 'plainfield', county: 'Hendricks County' },
  { name: 'Brownsburg', slug: 'brownsburg', county: 'Hendricks County' },
  { name: 'Greenwood', slug: 'greenwood', county: 'Johnson County' },
  { name: 'Zionsville', slug: 'zionsville', county: 'Boone County' },
  { name: 'Whitestown', slug: 'whitestown', county: 'Boone County' },
  { name: 'Lebanon', slug: 'lebanon', county: 'Boone County' },
  { name: 'Mooresville', slug: 'mooresville', county: 'Morgan County' },
  { name: 'Anderson', slug: 'anderson', county: 'Madison County' },
  { name: 'Muncie', slug: 'muncie', county: 'Delaware County' },
  { name: 'Columbus', slug: 'columbus', county: 'Bartholomew County' },
  { name: 'Bloomington', slug: 'bloomington', county: 'Monroe County' },
  { name: 'Terre Haute', slug: 'terre-haute', county: 'Vigo County' },
];

const countyGroups = [
  { name: 'Marion County', cities: ['Indianapolis', 'Castleton', 'Broad Ripple', 'Lawrence', 'Beech Grove', 'Speedway'] },
  { name: 'Hamilton County', cities: ['Carmel', 'Fishers', 'Noblesville', 'Westfield'] },
  { name: 'Marion / Hamilton County', cities: ['Geist'] },
  { name: 'Hendricks County', cities: ['Avon', 'Plainfield', 'Brownsburg'] },
  { name: 'Boone County', cities: ['Zionsville', 'Whitestown', 'Lebanon'] },
  { name: 'Johnson County', cities: ['Greenwood'] },
  { name: 'Morgan County', cities: ['Mooresville'] },
  { name: 'Madison County', cities: ['Anderson'] },
  { name: 'Delaware County', cities: ['Muncie'] },
  { name: 'Bartholomew County', cities: ['Columbus'] },
  { name: 'Monroe County', cities: ['Bloomington'] },
  { name: 'Vigo County', cities: ['Terre Haute'] },
];

export default function AllServicesPage() {

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': SITE_URL,
          name: 'Home'
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `${SITE_URL}/all-services`,
          name: 'All Services'
        }
      }
    ]
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Life Insurance Products & Services',
    description: 'Comprehensive life insurance solutions for Indiana families',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Service',
          '@id': `${SITE_URL}/term-life`,
          name: 'Term Life Insurance',
          description: 'Affordable coverage for 10-30 years to protect your family during critical earning years',
          url: `${SITE_URL}/term-life`
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Service',
          '@id': `${SITE_URL}/whole-life`,
          name: 'Whole Life Insurance',
          description: 'Lifetime protection with guaranteed cash value growth and fixed premiums',
          url: `${SITE_URL}/whole-life`
        }
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Service',
          '@id': `${SITE_URL}/universal-life`,
          name: 'Universal Life Insurance',
          description: 'Flexible permanent coverage with adjustable premiums and death benefits',
          url: `${SITE_URL}/universal-life`
        }
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Service',
          '@id': `${SITE_URL}/final-expense`,
          name: 'Final Expense Insurance',
          description: 'Simplified coverage for seniors to cover funeral costs and end-of-life expenses',
          url: `${SITE_URL}/final-expense`
        }
      },
      {
        '@type': 'ListItem',
        position: 5,
        item: {
          '@type': 'Service',
          '@id': `${SITE_URL}/mortgage-protection`,
          name: 'Mortgage Protection Insurance',
          description: 'Ensure your family can keep the home if something happens to you',
          url: `${SITE_URL}/mortgage-protection`
        }
      },
      {
        '@type': 'ListItem',
        position: 6,
        item: {
          '@type': 'Service',
          '@id': `${SITE_URL}/no-medical-exam`,
          name: 'No Medical Exam Life Insurance',
          description: 'Fast approval life insurance without medical exams or lengthy underwriting',
          url: `${SITE_URL}/no-medical-exam`
        }
      }
    ]
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/all-services#webpage`,
    url: `${SITE_URL}/all-services`,
    name: 'All Services - Hoosier Life Insurance',
    description: 'Comprehensive life insurance and financial services for Indiana families. Term life, whole life, final expense, annuities, and wealth building strategies.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    datePublished: '2024-01-15T00:00:00-05:00',
    dateModified: new Date().toISOString(),
    breadcrumb: { '@id': `${SITE_URL}/all-services#breadcrumb` }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What life insurance products does Hoosier Life Insurance offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Hoosier Life Insurance offers term life insurance, whole life insurance, final expense insurance, mortgage protection, and annuities. We also provide free wealth strategy consultations to help Indiana families build long-term financial security.',
        },
      },
      {
        '@type': 'Question',
        name: 'What areas of Indiana does Hoosier Life Insurance serve?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We serve 24 cities and communities across Central Indiana, including Indianapolis, Carmel, Fishers, Noblesville, Westfield, Avon, Plainfield, Brownsburg, Greenwood, Zionsville, Bloomington, Muncie, Anderson, Columbus, Terre Haute, and more.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I get a free life insurance quote in Indiana?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can get a free, no-obligation life insurance quote by booking a free wealth strategy call, calling us at (317) 648-2983, or filling out our online quote form. A licensed Indiana insurance advisor will review your needs and provide personalized options.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the most affordable life insurance option for Indiana families?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Term life insurance is typically the most affordable option for Indiana families. It provides substantial coverage at a low monthly cost for a set period — ideal for income replacement, mortgage protection, and protecting young families. Final expense insurance is the most affordable option for seniors on fixed incomes.',
        },
      },
    ],
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Indiana Life Insurance Services & Coverage Areas';

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Browse all life insurance products and Indiana service areas — term life, whole life, final expense, mortgage protection, annuities & 24 cities served.');
    setMeta('keywords', 'Indiana life insurance services, term life, whole life, final expense, mortgage protection, annuities, Indianapolis insurance, service areas Indiana');
    setMeta('geo.region', 'US-IN');
    setMeta('og:title', 'All Services & Coverage Areas | Hoosier Life Insurance', true);
    setMeta('og:description', 'Complete directory of life insurance products and Indiana service areas from Hoosier Life Insurance.', true);
    setMeta('og:url', `${SITE_URL}/all-services`, true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/all-services`);
    setMeta('last-modified', new Date().toISOString());

    // Inject JSON-LD schema scripts
    const schemas = [breadcrumbSchema, itemListSchema, webPageSchema, faqSchema];
    const scriptTags: HTMLScriptElement[] = [];
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptTags.push(script);
    });

    return () => {
      document.title = prevTitle;
      scriptTags.forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <title>All Life Insurance Services & Indiana Coverage Areas | Hoosier Life Insurance</title>
      <Header />

      {/* ── HERO ── */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white pt-40 pb-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-xs font-bold tracking-widest text-orange-400 uppercase mb-4">
            Complete Directory
          </span>
          <h1 className="text-5xl font-bold mb-5 leading-tight">
            All Services &amp; Coverage Areas
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
            Every insurance product we offer and every Indiana community we serve — all in one place.
          </p>
          {/* Breadcrumb */}
          <nav className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-400" aria-label="Breadcrumb">
            <a href="/" className="hover:text-orange-400 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white font-medium">All Services</span>
          </nav>
        </div>
      </section>

      {/* ── QUICK JUMP NAV ── */}
      <section className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {[
              { label: 'Insurance Products', href: '#products' },
              { label: 'Service Areas', href: '#service-areas' },
              { label: 'Resources', href: '#resources' },
              { label: 'Contact', href: '#contact-cta' },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="whitespace-nowrap px-4 py-2 text-sm font-semibold text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── INSURANCE PRODUCTS ── */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <span className="inline-block text-xs font-bold tracking-widest text-orange-600 uppercase mb-3">
              What We Offer
            </span>
            <h2 className="text-4xl font-bold text-slate-900 mb-3">
              <strong>Insurance Products</strong> &amp; Services
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl">
              Comprehensive life insurance solutions tailored to every stage of life and every budget.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {insuranceProducts.map((product) => (
              <Link
                key={product.href}
                to={product.href}
                className="group relative bg-white border border-gray-200 rounded-xl p-7 hover:border-orange-500 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                {product.badge && (
                  <span className="absolute top-4 right-4 text-[11px] font-bold uppercase tracking-wide bg-orange-100 text-orange-600 px-2.5 py-1 rounded-full">
                    {product.badge}
                  </span>
                )}
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 group-hover:bg-orange-100 transition-colors mb-5">
                  <i className={`${product.icon} text-2xl text-orange-600`} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 group-hover:text-orange-600 transition-colors mb-2">
                  {product.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                  {product.description}
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-orange-600 group-hover:gap-2 transition-all">
                  Learn More <i className="ri-arrow-right-line" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── DIVIDER ── */}
      <div className="max-w-7xl mx-auto px-6">
        <hr className="border-gray-100" />
      </div>

      {/* ── SERVICE AREAS ── */}
      <section id="service-areas" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
            <div>
              <span className="inline-block text-xs font-bold tracking-widest text-orange-600 uppercase mb-3">
                Where We Serve
              </span>
              <h2 className="text-4xl font-bold text-slate-900 mb-3">
                <strong>Indiana Service Areas</strong>
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl">
                Local life insurance experts serving 24 cities and neighborhoods across Central Indiana.
              </p>
            </div>
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-2 px-5 py-3 bg-orange-600 text-white text-sm font-bold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap self-start lg:self-auto"
            >
              <i className="ri-map-pin-2-line" />
              View Full Service Area Map
            </Link>
          </div>

          {/* County Groups */}
          <div className="space-y-10">
            {countyGroups.map((county) => (
              <div key={county.name}>
                <h3 className="flex items-center gap-2 text-base font-bold text-slate-700 uppercase tracking-wide mb-4">
                  <i className="ri-map-pin-2-fill text-orange-500 text-lg" />
                  {county.name}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {county.cities.map((cityName) => {
                    const city = cities.find((c) => c.name === cityName);
                    return city ? (
                      <Link
                        key={city.slug}
                        to={`/service-areas/${city.slug}`}
                        className="group flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-orange-50 hover:border-orange-400 hover:shadow-sm transition-all duration-200 cursor-pointer"
                      >
                        <span className="text-sm font-semibold text-slate-800 group-hover:text-orange-600 transition-colors leading-tight">
                          {city.name}
                        </span>
                        <i className="ri-arrow-right-s-line text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </Link>
                    ) : null;
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* All cities flat list for SEO */}
          <div className="mt-14 bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <i className="ri-list-check-2 text-orange-600 text-xl" />
              Complete City Directory — All 24 Locations
            </h3>
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {cities.map((city) => (
                <li key={city.slug}>
                  <Link
                    to={`/service-areas/${city.slug}`}
                    className="flex items-center gap-2 text-sm text-slate-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-map-pin-line text-orange-400 text-xs flex-shrink-0" />
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── DIVIDER ── */}
      <div className="max-w-7xl mx-auto px-6">
        <hr className="border-gray-100" />
      </div>

      {/* ── RESOURCES ── */}
      <section id="resources" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <span className="inline-block text-xs font-bold tracking-widest text-orange-600 uppercase mb-3">
              Helpful Pages
            </span>
            <h2 className="text-4xl font-bold text-slate-900 mb-3">
              <strong>Resources</strong> &amp; Legal
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {resourcePages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="group flex items-start gap-4 p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-400 hover:bg-orange-50 hover:shadow-md transition-all duration-200 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 group-hover:border-orange-300 transition-colors flex-shrink-0">
                  <i className={`${page.icon} text-lg text-orange-600`} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 group-hover:text-orange-600 transition-colors mb-1">
                    {page.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{page.description}</p>
                </div>
              </Link>
            ))}
          </div>
          {/* Sitemap & navigation helpers */}
          <div className="mt-10 p-6 bg-gray-50 rounded-xl border border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-slate-900 mb-1">Looking for a specific page?</p>
              <p className="text-xs text-gray-600">Browse our complete HTML sitemap — every product, blog post, and service area listed in one place.</p>
            </div>
            <a href="/sitemap" className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap">
              <i className="ri-road-map-line"></i>
              View Full Site Map
            </a>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="contact-cta" className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <i className="ri-shield-star-line text-5xl text-orange-400 mb-6 block" />
          <h2 className="text-4xl font-bold mb-5">
            Ready to Protect Your Family?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            Book a free strategy call with a licensed Indiana insurance advisor — no pressure, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/wealth-strategy-call"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-calendar-check-line" />
              Book Free Consultation
            </Link>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-lg hover:bg-white/20 transition-colors whitespace-nowrap border border-white/20"
            >
              <i className="ri-phone-line" />
              Call (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
